import React, { useEffect, useRef } from 'react';
import { useAppSelector } from '@/stores';
import { selectUser } from '@/stores/selectors';
import { useAuthContext } from '@/providers/auth-provider';
import { useAnalytics } from './provider';
import { event } from './events';
import type { SessionIntent } from './types';

const FIRST_LOGIN_KEY = 'analytics_first_login_seen';
const LAST_SESSION_KEY = 'analytics_last_session_at';

/**
 * Derive a coarse intent for this session. Refined definitions may emerge
 * from event sequences downstream, but a starting label here lets analysts
 * cohort users from day 1 instead of waiting for retro labelling.
 */
const deriveSessionIntent = (isFirstLogin: boolean): SessionIntent => {
    if (isFirstLogin) return 'first_setup';
    return 'passive';
};

/**
 * Watches auth state and fires identify() + group() once per authenticated
 * session. Renders nothing. Reset is called on logout so a subsequent login
 * does not leak prior user identity into the new session.
 *
 * `isAuthenticated` lives in AuthContext (not Redux) in this project, so we
 * read it via useAuthContext while the user record itself comes from Redux.
 */
export const AuthTracker: React.FC = () => {
    const analytics = useAnalytics();
    const { isAuthenticated } = useAuthContext();
    const user = useAppSelector(selectUser);

    const identifiedUserIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (isAuthenticated && user && String(user.id) !== identifiedUserIdRef.current) {
            const userId = String(user.id);
            identifiedUserIdRef.current = userId;

            const firstLoginSeen = (() => {
                try {
                    return localStorage.getItem(FIRST_LOGIN_KEY) === '1';
                } catch {
                    return false;
                }
            })();
            const isFirstLogin = !firstLoginSeen;
            const lastSessionAt = (() => {
                try {
                    return localStorage.getItem(LAST_SESSION_KEY);
                } catch {
                    return null;
                }
            })();
            const daysSinceLastSession = lastSessionAt
                ? Math.max(0, Math.floor((Date.now() - new Date(lastSessionAt).getTime()) / 86_400_000))
                : undefined;

            analytics.identify(userId, {
                role: user.role,
                is_first_login: isFirstLogin,
            });

            if (user.subdomain) {
                analytics.groupAccount(user.subdomain, {
                    subdomain: user.subdomain,
                    account_name: user.subdomain,
                });
            }

            analytics.track(event('Signed In', {
                is_first_login: isFirstLogin,
                session_intent: deriveSessionIntent(isFirstLogin),
                days_since_last_session: daysSinceLastSession,
            }));

            try {
                localStorage.setItem(FIRST_LOGIN_KEY, '1');
                localStorage.setItem(LAST_SESSION_KEY, new Date().toISOString());
            } catch {
                // localStorage failures must not affect auth or tracking.
            }
        }

        if (!isAuthenticated && identifiedUserIdRef.current !== null) {
            identifiedUserIdRef.current = null;
            analytics.reset();
        }
    }, [analytics, isAuthenticated, user]);

    return null;
};
